export type SceneId =
  | 'hall'
  | 'alimentation'
  | 'friandises'
  | 'comptoir'
  | 'reserve'
  | 'galerie';

export const SCENE_IDS: readonly SceneId[] = [
  'hall',
  'alimentation',
  'friandises',
  'comptoir',
  'reserve',
  'galerie',
];

/**
 * Pièces desservies par la barre de navigation, dans l'ordre de la visite.
 *
 * Alimentation et Friandises restent des salles à part entière, atteignables
 * par leur URL et par les portes du Hall : seules leurs entrées de menu sont
 * retirées, le temps que leur décor rejoigne celui de la rue de vitrines.
 */
export const NAV_SCENE_IDS: readonly SceneId[] = ['hall', 'comptoir', 'galerie', 'reserve'];

export type HotspotAction =
  | 'navigate'
  | 'dialogue'
  | 'inspect'
  | 'product'
  | 'collectible'
  | 'animation'
  | 'external-link';

export interface HotspotBounds {
  /** Position et taille en pourcentage de la scène, pour rester responsive. */
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ResponsivePosition {
  desktop: HotspotBounds;
  tablet?: HotspotBounds;
  mobile?: HotspotBounds;
}

export interface SceneHotspot extends HotspotBounds {
  id: string;
  label: string;
  action: HotspotAction;
  /** Cible : SceneId, id de dialogue, handle produit, URL… selon l'action. */
  target?: string;
  zIndex?: number;
  hint?: string;
  enabled?: boolean;
  /** Surcharges de position par breakpoint. */
  responsive?: Omit<ResponsivePosition, 'desktop'>;
  /** Libellé affiché dans la navigation simplifiée. */
  simplifiedLabel?: string;
}

export interface SceneLayer {
  id: string;
  src: string;
  /** 0 = fond immobile, 1 = premier plan le plus mobile. */
  depth: number;
  alt?: string;
  decorative?: boolean;
  /**
   * Position dans la scène, en pourcentage, comme les hotspots. Sans ces
   * valeurs le calque couvre toute la scène (cas du fond).
   */
  x?: number;
  y?: number;
  width?: number;
}

export type SceneTransition = 'slide' | 'door' | 'depth-fade' | 'walk';

export interface ShelfCollection {
  /** Handle de collection Shopify. */
  handle: string;
  label: string;
}

/**
 * Étagère alimentée par le catalogue : les niches sont remplies par le code,
 * pas déclarées une par une. Réservée à la scène qui absorbe tout le catalogue.
 */
export interface ShelfConfig {
  /** Zone des niches, en pourcentage de la scène. */
  x: number;
  y: number;
  width: number;
  height: number;
  columns: number;
  rows: number;
  /** Colonnes et rangées réduites sur petit écran. */
  mobileColumns?: number;
  mobileRows?: number;
  collections: ShelfCollection[];
}

/**
 * Allée de catégories : une étagère par collection, séparées par des passages,
 * défilement vertical à l'intérieur de chaque étagère et bouclage horizontal.
 */
export interface AisleConfig {
  collections: ShelfCollection[];
  /** Produits par niveau de tablette. */
  perLevel: number;
  mobilePerLevel: number;
  /** Nombre de produits demandés par catégorie. */
  maxProducts: number;
  /** Masquer les produits en rupture : ils restent visibles en Réserve. */
  hideUnavailable?: boolean;
  /** Sortie de la salle : l'allée n'a pas de décor fixe où poser un hotspot. */
  exit: { target: SceneId; label: string };
}

export interface SceneDefinition {
  id: SceneId;
  title: string;
  background: string;
  layers: SceneLayer[];
  hotspots: SceneHotspot[];
  shelf?: ShelfConfig;
  aisle?: AisleConfig;
  /**
   * Emplacements garnis par les meilleures ventes du moment. Le contenu vient
   * de Shopify, pas de la définition : il change tout seul.
   */
  featured?: { positions: HotspotBounds[] };
  /** Largeur / hauteur du décor. 16/9 par défaut ; plus large = panorama. */
  aspectRatio?: number;
  /** Fond répété horizontalement au lieu d'une image unique étirée. */
  backgroundRepeat?: boolean;
  ambientSound?: string;
  entryAnimation?: SceneTransition;
  /** Réplique jouée à la première entrée dans la scène. */
  entryDialogueId?: string;
}
